import React, { useMemo } from 'react';
import { BrandColor } from '@styleseat/brand';
import { Inline } from '@styleseat/ui';
import moment, { MomentInput } from 'moment';
import Title from './Title';
import {
  BodyText,
  LineClamp,
  Stack,
  Touchable,
} from '../../../../ui';
import ConditionalWrapper from '../../../../ui/ConditionalWrapper';
import { ShoppingBagIcon } from '../../../../ui/Icons/ShoppingBagIcon';
import { BasicDropIcon } from '../../../../ui/Icons/BasicDropIcon';
import type { NoteClickParams } from '../ActivityHistory';
import { FileNoteTextIcon } from '../../../../ui/Icons/FileNoteTextIcon';

export type NoteItemProps = {
  onClick?: (params: NoteClickParams) => void;
  type?: string;
  date?: MomentInput;
  blurb: string;
  renderTitle?: () => string;
};

type VisualProperties = {
  icon?: React.FunctionComponent<{ color?: string; width?: number; height?: number; }>;
  title?: string;
  color: string;
  backgroundColor?: string;
};

const NOTE_APPEARANCES: Record<string, VisualProperties> = {
  products: {
    title: 'Product',
    icon: ShoppingBagIcon,
    color: BrandColor.Metal,
    backgroundColor: BrandColor.SuperLight,
  },
  formula: {
    title: 'Formula',
    icon: BasicDropIcon,
    color: BrandColor.Metal,
    backgroundColor: BrandColor.SuperLight,
  },
  treatment: {
    title: 'Treatment',
    icon: BasicDropIcon,
    color: BrandColor.Metal,
    backgroundColor: BrandColor.SuperLight,
  },
  session: {
    title: 'Session',
    color: BrandColor.Metal,
    backgroundColor: BrandColor.SuperLight,
  },
  activities: {
    title: 'Activities',
    color: BrandColor.Metal,
    backgroundColor: BrandColor.SuperLight,
  },
  note: {
    icon: FileNoteTextIcon,
    color: BrandColor.Metal,
    backgroundColor: BrandColor.SuperLight,
  },
  client: {
    icon: FileNoteTextIcon,
    color: BrandColor.Metal,
    backgroundColor: BrandColor.WarningBackground,
  },
};

const NoteItem: React.FC<NoteItemProps> = ({
  onClick,
  type,
  date,
  blurb,
  renderTitle,
}) => {
  const formattedDate = useMemo(() => (
    date ? moment(date).format('MMM D, YYYY') : null
  ), [date]);

  const {
    title,
    icon,
    color,
    backgroundColor,
  } = useMemo(() => {
    const noteType = (type || '').toLowerCase();
    return NOTE_APPEARANCES[noteType] || {
      title: undefined,
      icon: undefined,
      color: undefined,
      backgroundColor: undefined,
    };
  }, [type]);

  const noteTitle = useMemo(() => {
    const defaultTitle = title ? `${title} Note` : 'Note';
    return typeof renderTitle === 'function' ? (
      renderTitle()
    ) : defaultTitle;
  }, [
    title,
    renderTitle,
  ]);

  const handleClick = () => {
    onClick?.({
      title: noteTitle,
      text: blurb,
      isClientNote: type === 'client',
    });
  };

  return (
    <ConditionalWrapper
      condition={typeof onClick === 'function'}
      wrapper={child => (
        <Touchable onClick={handleClick} testID="NoteItem-container">
          {child}
        </Touchable>
      )}
    >
      <Stack
        flexGrow={1}
        flexShrink={1}
        space={8}
        testID="NoteItem-container"
      >
        <Inline
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Title
            color={color}
            backgroundColor={backgroundColor}
            IconComponent={icon}
          >
            {noteTitle}
          </Title>
          {formattedDate ? (
            <BodyText
              appearance="smaller"
              color={BrandColor.Ash}
              testID="HistoryItem--date"
            >
              {formattedDate}
            </BodyText>
          ) : null}
        </Inline>
        {blurb ? (
          <LineClamp clamp={2}>
            <BodyText
              appearance="small"
              color={BrandColor.Ash}
            >
              {blurb}
            </BodyText>
          </LineClamp>
        ) : null}
      </Stack>
    </ConditionalWrapper>
  );
};

export default NoteItem;
